import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { performanceAPI, employeeAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../components/EmployeeManagement.css';

const PerformanceManagement = () => {
  const { currentUser, hasPermission } = useAuth();
  const isPrivileged = hasPermission('employee_view');
  const [showModal, setShowModal] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ employee: '', reviewPeriod: new Date().toISOString().slice(0, 7), overallRating: 3, comments: '', status: 'normal' });

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const res = await performanceAPI.getAll();
      setReviews(res?.data || res || []);
    } catch (e) { setError('Failed to load reviews'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchReviews();
    if (isPrivileged) employeeAPI.getAll().then(r => setEmployees(r?.data || r || [])).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await performanceAPI.create(form);
      setShowModal(false);
      setForm({ employee: '', reviewPeriod: new Date().toISOString().slice(0, 7), overallRating: 3, comments: '', status: 'normal' });
      fetchReviews();
    } catch (err) { setError('Failed to save review'); }
  };

  const renderStars = (rating) => [...Array(5)].map((_, i) => (
    <i key={i} className={i < rating ? 'fas fa-star' : 'far fa-star'} style={{ color: '#fbbf24', fontSize: 14 }}></i>
  ));

  return (
    <div className="performance-management">
      <div className="module-header">
        <h2>Performance Management</h2>
        {isPrivileged && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <i className="fas fa-plus"></i> Add Review
          </button>
        )}
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 16px', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#6b7280' }}><i className="fas fa-spinner fa-spin" style={{ fontSize: 28 }}></i><p>Loading...</p></div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                {isPrivileged && <th>Employee</th>}
                <th>Review Period</th>
                <th>Rating</th>
                <th>Status</th>
                <th>Comments</th>
                <th>Reviewer</th>
              </tr>
            </thead>
            <tbody>
              {reviews.length === 0
                ? <tr><td colSpan="6" style={{ textAlign: 'center', color: '#9ca3af', padding: 30 }}>No performance reviews found</td></tr>
                : reviews.map(r => (
                  <tr key={r._id}>
                    {isPrivileged && <td>{r.employee?.name || '—'}</td>}
                    <td>{r.reviewPeriod ? new Date(r.reviewPeriod).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '—'}</td>
                    <td><div style={{ display: 'flex', gap: 2 }}>{renderStars(r.overallRating || 0)}</div></td>
                    <td>
                      <span style={{ padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, textTransform: 'capitalize',
                        background: r.status === 'promotion' ? '#d1fae5' : r.status === 'warning' ? '#fee2e2' : '#f3f4f6',
                        color: r.status === 'promotion' ? '#065f46' : r.status === 'warning' ? '#991b1b' : '#374151'
                      }}>{r.status || 'normal'}</span>
                    </td>
                    <td style={{ maxWidth: 200, fontSize: 13, color: '#6b7280' }}>{r.comments || '—'}</td>
                    <td>{r.reviewer?.name || '—'}</td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}

      {isPrivileged && (
        <Modal show={showModal} onClose={() => setShowModal(false)} title="Add Performance Review">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Employee</label>
              <select className="form-input" value={form.employee} onChange={e => setForm({ ...form, employee: e.target.value })} required>
                <option value="">Select Employee</option>
                {employees.map(emp => <option key={emp._id} value={emp._id}>{emp.name} ({emp.employeeId})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Review Period</label>
              <input type="month" className="form-input" value={form.reviewPeriod} onChange={e => setForm({ ...form, reviewPeriod: e.target.value })} required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Overall Rating</label>
                <select className="form-input" value={form.overallRating} onChange={e => setForm({ ...form, overallRating: parseInt(e.target.value) })} required>
                  <option value="5">5 - Excellent</option>
                  <option value="4">4 - Very Good</option>
                  <option value="3">3 - Good</option>
                  <option value="2">2 - Average</option>
                  <option value="1">1 - Poor</option>
                </select>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select className="form-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="normal">Normal</option>
                  <option value="promotion">Promotion</option>
                  <option value="warning">Warning</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Comments</label>
              <textarea className="form-input" rows="3" placeholder="Review comments..." value={form.comments} onChange={e => setForm({ ...form, comments: e.target.value })} required></textarea>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary">Submit Review</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default PerformanceManagement;
